import { Component, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { SpinnerService } from '../../../core/services/spinner.service';
import { ManagementsService } from '../services/managements.service';
import { ModalService } from '../../shared/services/modal.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputComponent } from '../../shared/ui/input/input.component';
import { SelectData } from '../../shared/type/input';
import { PopoverController } from '@ionic/angular/standalone';
import { GeneralErrorComponent } from '../../shared/ui/general-error/general-error.component';
import { DataLayerService } from '../../../core/services/data-layer.service';

@Component({
  selector: 'app-managements-users',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    ReactiveFormsModule,
    InputComponent,
    GeneralErrorComponent,
  ],
  providers: [],
  templateUrl: './managements-users.component.html',
  styleUrl: './managements-users.component.scss',
})
export class ManagementsUsersComponent implements OnDestroy {
  screenName = 'my-managements-users';
  usersExt!: UserExt[];
  filterUserForm!: FormGroup;
  subscription: Subscription = new Subscription();

  @Input() filterNameList: SelectData[] = [];
  @Input() filterCompanyList: SelectData[] = [];
  stateCompanyList: SelectData[] = [];

  filterSelected: FiltersParams = {};
  defaultValueSelect = { value: '', label: 'Todos' };
  hasMoreItems: boolean = false;
  nextPage = '';

  constructor(
    private readonly _managementsService: ManagementsService,
    private readonly _spinnerService: SpinnerService,
    private readonly _modalService: ModalService,
    private readonly _formBuilder: FormBuilder,
    public popoverController: PopoverController,
    private readonly _dataLayerService: DataLayerService
  ) {
    this.getUsersExt();
  }

  ngOnInit(): void {
    this.filterUserForm = this._formBuilder.group({
      userEmail: [''],
      company: [''],
      state: [''],
    });

    this.stateCompanyList = this._managementsService.statusUserData.sort(
      (a, b) => a.label.localeCompare(b.label)
    );
    this.filter();
  }

  filter(): void {
    this.filterUserForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((prev, curr) => {
          return JSON.stringify(prev) === JSON.stringify(curr);
        })
      )
      .subscribe((value) => {
        const filters: FiltersParams = {};
        let elementId: string = '';

        if (value.userEmail) {
          filters.userEmail = value.userEmail;
          elementId = 'userEmail';
        }
        if (value.company) {
          filters.userCompany = value.company;
          elementId = 'company';
        }
        if (value.state) {
          filters.state = parseInt(value.state, 10);
          elementId = 'state';
        }
        this.filterSelected = filters;
        const elementValue = elementId === 'userEmail' ? '' : value[elementId];
        const elementText = elementValue
          ? `${elementId}:${elementValue}`
          : elementId;
        this._dataLayerService.pushEventSearch(
          this.screenName,
          `search-${elementId}`,
          elementText
        );
        this.getUsersExt(filters, false);
      });
  }

  postUsersExt(email: string): void {
    this._spinnerService.show();
    const sub = this._managementsService.postManageUserExt(email).subscribe({
      next: (res) => {
        this._modalService.showSuccessModalService(() => {
          this.getUsersExt(this.filterSelected, false);
        });
        this._dataLayerService.pushEventSubmit(
          this.screenName,
          'manage_users_ext',
          'Post external user',
          'content actions'
        );
      },
      error: (err) => {
        this._spinnerService.hide();
        this._dataLayerService.pushEventSubmitError(
          this.screenName,
          'manage_users_ext',
          err.status,
          err.error?.message ?? err.status
        );
      },
      complete: () => {
        this._spinnerService.hide();
      },
    });
    this.subscription.add(sub);
  }

  getUsersExt(useFilters: FiltersParams = {}, seeMore: boolean = false): void {
    this._spinnerService.show();
    if (this.nextPage && seeMore) {
      useFilters.pageId = this.nextPage;
    }
    const sub = this._managementsService
      .getManageUserExt(useFilters)
      .subscribe({
        next: (res) => {
          const response: GetUsersExtResponse = res;
          // registrar evento submit
          const description = seeMore ? 'Load more users' : 'Search users';
          this._dataLayerService.pushEventSubmit(
            this.screenName,
            'manage_users_ext',
            description,
            'search performed'
          );

          if (seeMore) {
            this.usersExt = [...(this.usersExt ?? []), ...response.users];
          } else {
            this.usersExt = response.users ?? [];
          }

          this.nextPage = response.pageId;

          if (this.nextPage) {
            this.hasMoreItems = true;
          } else {
            this.hasMoreItems = false;
          }
        },
        error: (err) => {
          this.usersExt = [];
          this.nextPage = '';
          this.hasMoreItems = false;
          this._spinnerService.hide();
          this._dataLayerService.pushEventSubmitError(
            this.screenName,
            'manage_users_ext',
            err.status,
            err.error?.message ?? err.status
          );
        },
        complete: () => {
          this._spinnerService.hide();
        },
      });
    this.subscription.add(sub);
  }

  loadMore(): void {
    // registrar evento click button load more
    this._dataLayerService.pushEventClickButton(
      this.screenName,
      'load_more_users',
      'Load more users button',
      'content actions'
    );
    this.getUsersExt(this.filterSelected, true);
  }

  openOptionsModal(e: Event, email: string) {
    this._managementsService.openPopover(
      e,
      this._managementsService.userModalData(() => {
        // registrar evento click button archivar
        this._dataLayerService.pushEventClickButton(
          this.screenName,
          'archive_user',
          'Archive external user',
          'content actions'
        );
        this.showSuccessModal(email);
      })
    );
  }

  showSuccessModal(email: string): void {
    this._modalService.openGenericModal(
      this._managementsService.archiveModalData(
        () => {},
        () => {
          this.postUsersExt(email);
        }
      )
    );
  }

  getState(state: number): string {
    const status = this.stateCompanyList.find(
      (item) => item.value === state.toString()
    );
    return status ? status.label : 'Desconocido';
  }

  getCompany(value: number): string {
    const status = this.filterCompanyList.find(
      (item) => item.value === value.toString()
    );
    return status ? status.label : 'Todos';
  }

  getName(value: number): string {
    const status = this.filterNameList.find(
      (item) => item.value === value?.toString()
    );
    return status ? status.label : 'Todos';
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
